import { addDays, format } from 'date-fns';
import { supabase } from '../lib/supabase';

const RECIPE_PHOTOS_BUCKET = 'recipe-photos';

// Gemigreerde photo_url-rijen (vóór de foto-galerij-feature) bevatten een
// externe URL i.p.v. een pad in de recipe-photos-bucket; onderscheiden op
// basis van het http(s)-prefix (zelfde patroon als RecipeFormPage.jsx en
// app/src/services/database.js).
export function getRecipePhotoUrl(storagePath) {
  if (!storagePath) return null;
  if (storagePath.startsWith('http://') || storagePath.startsWith('https://')) return storagePath;
  return supabase.storage.from(RECIPE_PHOTOS_BUCKET).getPublicUrl(storagePath).data.publicUrl;
}

export function todayISO() {
  return format(new Date(), 'yyyy-MM-dd');
}

export function addDaysISO(dateStr, days) {
  return format(addDays(new Date(`${dateStr}T00:00:00`), days), 'yyyy-MM-dd');
}

// Vindt de kalenderdatum van day_of_week (0=zo…6=za, JS Date#getDay()-
// conventie, gelijk aan de EXTRACT(DOW)-conventie van de materialisatie-cron)
// binnen "deze week" — voor een herhalend schema de lopende zo-za
// kalenderweek, voor een eenmalig schema de exacte 7-daagse periode
// valid_from..valid_until. Identiek aan WeekschemaScreen.js (app).
// isRecurring wordt EXPLICIET meegegeven (niet uit schedule.is_recurring
// gelezen): de radiobutton bovenaan het scherm is de bron van waarheid voor de
// sessie-intentie en kan tijdelijk afwijken van de nog niet opgeslagen
// schedule-rij — anders resolvet deze functie data op basis van een
// verouderde DB-waarde (o.a. de "Zo niet gevuld"-bug bij Kopieer-naar-alle-
// dagen, zie BRIEFING.md hoofdstuk 7).
export function resolveDateForDayOfWeek(dayOfWeek, schedule, startOfWeekFn, isRecurring) {
  const base = isRecurring
    ? startOfWeekFn(new Date(), { weekStartsOn: 0 })
    : new Date(`${schedule.valid_from}T00:00:00`);
  for (let offset = 0; offset < 7; offset++) {
    const d = addDays(base, offset);
    if (d.getDay() === dayOfWeek) return format(d, 'yyyy-MM-dd');
  }
  return null;
}

export const MEAL_TYPE_KEYS = [
  { key: 'breakfast', label: 'mealBreakfast' },
  { key: 'snack_morning', label: 'mealSnackMorning' },
  { key: 'lunch', label: 'mealLunch' },
  { key: 'snack_afternoon', label: 'mealSnackAfternoon' },
  { key: 'dinner', label: 'mealDinner' },
  { key: 'evening_snack', label: 'mealEveningSnack' },
];

export const DINNER_COMPONENT_KEYS = {
  protein: 'dinnerProtein',
  carbs: 'dinnerCarbs',
  vegetables: 'dinnerVegetables',
};

// Zelfde velden als useAuth.js (app): is_active (account-status) en
// client_status === 'prospect' (nog geen actief abonnement) — apart van
// blocked_at, dat elders al ad-hoc wordt opgevraagd (bv. client/RoosterPage.jsx).
export async function getClientProfileFlags(userId) {
  const { data } = await supabase.from('profiles').select('is_active, client_status').eq('id', userId).maybeSingle();
  return {
    isActive: data?.is_active !== false,
    isProspect: data?.client_status === 'prospect',
  };
}

export async function getCalorieGoal(clientId) {
  const { data } = await supabase.from('calorie_assignments').select('daily_calories').eq('client_id', clientId).maybeSingle();
  return data?.daily_calories || 2000;
}

// Kalenderindicatoren: 1 query per geopende maand i.p.v. per dag.
// Retourneert { [log_date]: aantal ingevulde eetmomenten } (0 t/m 6) t.b.v.
// de kleurcodering van de indicator-bolletjes (groen=vol, oranje=gedeeltelijk).
export async function getMealLogCounts(userId, startDate, endDate) {
  const { data, error } = await supabase
    .from('daily_meal_logs')
    .select('log_date')
    .eq('user_id', userId)
    .gte('log_date', startDate)
    .lte('log_date', endDate);
  if (error) throw error;
  const counts = {};
  (data || []).forEach((row) => {
    counts[row.log_date] = (counts[row.log_date] || 0) + 1;
  });
  return counts;
}

export function monthRangeISO(dateStr) {
  const [y, m] = dateStr.split('-').map(Number);
  const first = `${y}-${String(m).padStart(2, '0')}-01`;
  const lastDay = new Date(y, m, 0).getDate();
  const last = `${y}-${String(m).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
  return { first, last };
}

export function addMonthsISO(dateStr, n) {
  const [y, m] = dateStr.split('-').map(Number);
  const d = new Date(y, m - 1 + n, 1);
  return format(d, 'yyyy-MM-dd');
}
